import { BaseComponentImpl, RoomImpl, LevelImpl } from "../types/implementations";
import { BaseComponent, Building, BuildingModel } from "../types/protocols";
import { BaseType } from "../types/Enums";
import {
    filterUtilitiesInRoom,
    filterFixedFurnishingsInRoom,
    filterMembranesInRoom,
    filterOpeningsInRoom
} from "../Pages/Room/componentFilters";

/**
 * Interface for objects that may have a roomKey field.
 * Handles both iOS legacy format (plain string) and web format (ValueWithMetadata).
 */
interface ComponentWithRoomKey {
    roomKey?: string | { value?: string | null } | null;
}

/**
 * Helper to extract roomKey from a component, handling both plain string
 * (legacy iOS format) and ValueWithMetadata format (correct format)
 */
export function getComponentRoomKey(component: ComponentWithRoomKey | null | undefined): string | null {
    if (!component) return null;
    const roomKey = component.roomKey;
    if (!roomKey) return null;

    // Handle plain string (iOS legacy format)
    if (typeof roomKey === 'string') {
        return roomKey;
    }

    // Handle ValueWithMetadata format (correct format)
    return roomKey.value ?? null;
}

/**
 * Check if a component belongs to a specific room based on parentSurface relationships
 * Similar to iOS implementation where components can belong to rooms through:
 * 1. Direct roomKey relationship
 * 2. ANY parentSurface field (parentSurface, parentSurface1, or parentSurface2) 
 *    pointing to a surface component that belongs to the room
 * 
 * This ensures that components are properly associated with rooms regardless of 
 * which parentSurface field is used to define the relationship.
 */
export function componentBelongsToRoom(
    component: BaseComponentImpl, 
    room: RoomImpl, 
    building: Building
): boolean {
    // Check direct roomKey relationship
    if (getComponentRoomKey(component) === room.key) {
        return true;
    }

    // Check ALL parentSurface relationships - if ANY parentSurface belongs to the room,
    // then the component belongs to the room
    const parentSurfaces = [
        component.parentSurface?.value,
        component.parentSurface1?.value,
        component.parentSurface2?.value
    ].filter(Boolean); // Remove undefined values

    for (const parentSurfaceKey of parentSurfaces) {
        if (!parentSurfaceKey) continue;

        // Find the parent surface component
        const parentSurfaceComponent = building.model.structure?.components?.[parentSurfaceKey];
        if (!parentSurfaceComponent) continue;

        // Check if the parent surface component belongs to the room
        const parentRoomKey = getComponentRoomKey(parentSurfaceComponent);
        if (parentRoomKey === room.key) {
            return true;
        }
    }

    return false;
}

/**
 * Get all components that belong to a specific room
 */
export function getComponentsForRoom(
    room: RoomImpl,
    building: Building
): BaseComponentImpl[] {
    if (!building.model.structure?.components) {
        return [];
    }

    const components: BaseComponentImpl[] = [];

    Object.values(building.model.structure.components).forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        
        if (componentBelongsToRoom(component, room, building)) {
            components.push(component);
        }
    });

    return components;
}

/**
 * Get all components with blueprint locations that belong to a specific room
 */
export function getComponentsWithBlueprintLocationsForRoom(
    room: RoomImpl,
    building: Building,
    blueprintKey: string
): BaseComponentImpl[] {
    const components = getComponentsForRoom(room, building);
    
    return components.filter(component => {
        // Check if component has blueprint locations for the specified blueprint
        const blueprintLocations = (component as any).blueprintLocations;
        return blueprintLocations && blueprintLocations[blueprintKey];
    });
}

/**
 * Find the room that a component belongs to based on parentSurface relationships
 * Returns the room key or null if no room is found
 * 
 * Checks ALL parentSurface fields (parentSurface, parentSurface1, parentSurface2)
 * to determine which room the component belongs to.
 */
export function findRoomForComponent(
    component: BaseComponentImpl,
    building: Building
): string | null {
    // Check direct roomKey relationship
    const directRoomKey = getComponentRoomKey(component);
    if (directRoomKey) {
        return directRoomKey;
    }

    // Check ALL parentSurface relationships
    const parentSurfaces = [
        component.parentSurface?.value,
        component.parentSurface1?.value,
        component.parentSurface2?.value
    ].filter(Boolean);

    for (const parentSurfaceKey of parentSurfaces) {
        if (!parentSurfaceKey) continue;

        const parentSurfaceComponent = building.model.structure?.components?.[parentSurfaceKey];
        const parentRoomKey = getComponentRoomKey(parentSurfaceComponent);
        if (parentRoomKey) {
            return parentRoomKey;
        }
    }

    return null;
}

/**
 * Get the first parent-surface key that a component is placed on, if any.
 * Checks parentSurface, parentSurface1, parentSurface2 in order.
 *
 * Used as a secondary placement signal: when a component has no room, the
 * surface it sits on (e.g. the facade a window is placed on) is the next-best
 * way to describe where it is.
 */
export function getFirstParentSurfaceKey(
    component: BaseComponentImpl
): string | null {
    const parentSurfaceKeys = [
        component.parentSurface?.value,
        component.parentSurface1?.value,
        component.parentSurface2?.value
    ].filter(Boolean) as string[];

    return parentSurfaceKeys.length > 0 ? parentSurfaceKeys[0] : null;
}

/**
 * Resolve the surface a component is placed on (its key and display name), if any.
 * Returns null when the component isn't placed on another component.
 *
 * Use as a secondary placement signal when a component has no room
 * (e.g. show which facade a window sits on, and link to it).
 */
export function resolveParentSurface(
    component: BaseComponentImpl,
    building: Building
): { key: string; name: string } | null {
    const parentKey = getFirstParentSurfaceKey(component);
    if (!parentKey) return null;

    const parentData = building.model.structure?.components?.[parentKey];
    if (!parentData) return null;

    return { key: parentKey, name: new BaseComponentImpl(parentData).displayName(building) };
}

/**
 * Get all components that belong to one or more rooms using the same comprehensive
 * algorithm as RoomPage. This includes:
 * - Utilities (via parentRoomKeys)
 * - Fast inredning (via parentRoomKeys)
 * - Membranes (via parentRoomKeys)
 * - Windows/Doors (via parentSurface relationships)
 * - Surfaces (Ceiling, Wall, Floor via roomKey)
 * 
 * Components are deduplicated by key across all rooms.
 * 
 * @param building - The building model
 * @param roomKeys - Array of room keys to get components for
 * @returns Array of BaseComponentImpl instances, deduplicated by component key
 */
export function getAllComponentsForRooms(
    building: Building,
    roomKeys: string[]
): BaseComponentImpl[] {
    if (!building.model.structure?.components || roomKeys.length === 0) {
        return [];
    }

    const components = building.model.structure.components;
    const componentMap = new Map<string, BaseComponentImpl>();

    // Aggregate components from all specified rooms
    roomKeys.forEach(roomKey => {
        // 1. Utilities (via parentRoomKeys)
        const utilities = filterUtilitiesInRoom(building, roomKey);
        utilities.forEach(utilityData => {
            const utility = new BaseComponentImpl(utilityData);
            if (!componentMap.has(utility.key)) {
                componentMap.set(utility.key, utility);
            }
        });

        // 1b. Fast inredning (via parentRoomKeys)
        filterFixedFurnishingsInRoom(building, roomKey).forEach(furnishingData => {
            const furnishing = new BaseComponentImpl(furnishingData);
            if (!componentMap.has(furnishing.key)) {
                componentMap.set(furnishing.key, furnishing);
            }
        });

        // 2. Membranes (via parentRoomKeys)
        const membranes = filterMembranesInRoom(building, roomKey);
        membranes.forEach(membraneData => {
            const membrane = new BaseComponentImpl(membraneData);
            if (!componentMap.has(membrane.key)) {
                componentMap.set(membrane.key, membrane);
            }
        });

        // 3. Windows (via parentSurface relationships)
        const windows = filterOpeningsInRoom(building, roomKey, BaseType.Window);
        windows.forEach(windowData => {
            const window = new BaseComponentImpl(windowData);
            if (!componentMap.has(window.key)) {
                componentMap.set(window.key, window);
            }
        });

        // 4. Doors (via parentSurface relationships)
        const doors = filterOpeningsInRoom(building, roomKey, BaseType.Door);
        doors.forEach(doorData => {
            const door = new BaseComponentImpl(doorData);
            if (!componentMap.has(door.key)) {
                componentMap.set(door.key, door);
            }
        });

        // 5. Surfaces (Ceiling, Wall, Floor) - direct roomKey relationship
        Object.values(components).forEach(componentData => {
            const component = new BaseComponentImpl(componentData);
            if (
                (component.type === BaseType.Ceiling ||
                 component.type === BaseType.Wall ||
                 component.type === BaseType.Floor) &&
                getComponentRoomKey(component) === roomKey &&
                !componentMap.has(component.key)
            ) {
                componentMap.set(component.key, component);
            }
        });
    });

    return Array.from(componentMap.values());
}

/**
 * Result of resolving room and level for a component
 */
export interface ComponentLocationInfo {
    roomDisplay: string | null;
    levelDisplay: string | null;
    roomKey: string | null;
    levelKey: string | null;
}

/**
 * The first room reached through the component's parents (parentSurface,
 * parentSurface1, parentSurface2, then a lock's parentKey), each parent's own
 * roomKey first, then its parents up to `hops` levels down. An exterior parent
 * (facade, roof) has no room, so an exterior-first opening falls through to its
 * indoor side.
 */
const roomKeyThroughParents = (
    component: BaseComponent,
    components: { [key: string]: BaseComponent },
    hops: number
): string | null => {
    if (hops <= 0) return null;
    const parentKeys = [
        component.parentSurface?.value,
        component.parentSurface1?.value,
        component.parentSurface2?.value,
        component.parentKey?.value
    ].filter(Boolean) as string[];
    for (const parentKey of parentKeys) {
        const parent = components[parentKey];
        if (!parent || parent === component) continue;
        const roomKey = getComponentRoomKey(parent) || roomKeyThroughParents(parent, components, hops - 1);
        if (roomKey) return roomKey;
    }
    return null;
};

const INDOOR_SURFACE_TYPES = new Set<BaseType>([BaseType.Wall, BaseType.Ceiling, BaseType.Floor]);

/**
 * Whether the component is placed so that it belongs in a room: then a missing
 * room is a gap (shown as "–"), otherwise the component has no room by design
 * and Våning/Rum are left out.
 * - Walls, floors, ceilings, fast inredning and membranes are always room-bound.
 * - A utility only when it is placed in a room; mounted (roof, facade) and
 *   outdoor utilities have no room by definition.
 * - An Övrigt when it is in a room or mounted on a wall, ceiling or floor.
 * - A window or door when one of its parents is a wall, ceiling or floor; a
 *   facade-only opening is exterior.
 * - A lock when the door or window it sits on is.
 * - Every other base type (facade, roof, foundation, outdoor …) never has a room.
 */
export function belongsInRoom(
    component: BaseComponent,
    structure: BuildingModel['structure']
): boolean {
    const components = structure?.components ?? {};
    const hasIndoorParent = (keys: (string | null | undefined)[]) =>
        keys.some(key => !!key && INDOOR_SURFACE_TYPES.has(components[key]?.type));
    const hasRoomKeys = (component.parentRoomKeys?.value?.length ?? 0) > 0;

    switch (component.type) {
        case BaseType.Wall:
        case BaseType.Floor:
        case BaseType.Ceiling:
        case BaseType.FixedFurnishing:
        case BaseType.Membrane:
            return true;
        case BaseType.Utility:
            return hasRoomKeys;
        case BaseType.Miscellaneous:
            return hasRoomKeys || hasIndoorParent([component.parentSurface?.value]);
        case BaseType.Window:
        case BaseType.Door:
            return hasIndoorParent([
                component.parentSurface?.value,
                component.parentSurface1?.value,
                component.parentSurface2?.value
            ]);
        case BaseType.LockMechanism: {
            const parentKey = component.parentKey?.value;
            const parent = parentKey ? components[parentKey] : undefined;
            return !!parent && parent.type !== BaseType.LockMechanism && belongsInRoom(parent, structure);
        }
        default:
            return false;
    }
}

/**
 * Resolve the effective room and level for a component through the parent hierarchy.
 * Resolution order for room:
 * 1. Component's own roomKey
 * 2. Component's parentRoomKeys (first one)
 * 3. Parent surface's roomKey (checking parentSurface, parentSurface1, parentSurface2),
 *    or for a lock the room of the door/window it sits on
 *
 * @param component - The component to resolve location for
 * @param structure - The building structure containing components, rooms, and levels
 * @returns The resolved room and level display names, or null if not found
 */
export function resolveComponentLocation(
    component: BaseComponent,
    structure: BuildingModel['structure']
): ComponentLocationInfo {
    const result: ComponentLocationInfo = {
        roomDisplay: null,
        levelDisplay: null,
        roomKey: null,
        levelKey: null
    };

    if (!structure) return result;

    // 1. Get effective room key from component (handles both plain string and ValueWithMetadata)
    let effectiveRoomKey = getComponentRoomKey(component);

    // 2. Try parentRoomKeys if no direct roomKey
    if (!effectiveRoomKey && component.parentRoomKeys?.value && component.parentRoomKeys.value.length > 0) {
        effectiveRoomKey = component.parentRoomKeys.value[0];
    }

    // 3. Try to get room key from parent surface if not directly set
    // Check ALL parent surfaces until we find one with a roomKey
    // (matches filtering logic in componentFilters.ts). A lock points at its
    // door or window with `parentKey`, which in turn sits on a wall: one more hop.
    if (!effectiveRoomKey && structure.components) {
        effectiveRoomKey = roomKeyThroughParents(component, structure.components, 2);
    }

    // 4. Resolve room and level from room key
    if (effectiveRoomKey && structure.rooms) {
        const roomData = structure.rooms[effectiveRoomKey];
        if (roomData) {
            const room = new RoomImpl(roomData);
            result.roomDisplay = room.displayName();
            result.roomKey = effectiveRoomKey;

            if (room.levelKey && structure.levels) {
                const levelData = structure.levels[room.levelKey];
                if (levelData) {
                    result.levelDisplay = new LevelImpl(levelData).displayName();
                    result.levelKey = room.levelKey;
                }
            }
        }
    }

    return result;
}
